import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Package, Heart, MapPin, CreditCard, Bell, ChevronRight, Edit2, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { useCurrency } from '@/lib/currency-context';
import { orders as ordersApi } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';

const DashboardPage = () => {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersApi.list();
      setOrders(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { icon: Package, label: t('dashboard.myOrders'), href: '/orders', count: orders.length },
    { icon: Heart, label: t('dashboard.wishlist'), href: '/account/wishlist' },
    { icon: ChefHat, label: t('dashboard.savedRestaurants', 'Kaydettiğim Restoranlar'), href: '/account/saved-restaurants' },
    { icon: MapPin, label: t('dashboard.addresses'), href: '/account/settings' },
  ];

  return (
    <div className="py-8" data-testid="dashboard-page">
      <div className="container mx-auto px-4 md:px-8">
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-8">{t('dashboard.myAccount')}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Profile card */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Avatar className="w-20 h-20 mx-auto mb-4">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary text-white text-2xl">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-xl font-semibold">{user?.name}</h2>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <Badge variant="secondary" className="mt-2 capitalize">{user?.role}</Badge>
                  <Link to="/account/settings">
                    <Button variant="outline" size="sm" className="mt-4 rounded-full" data-testid="edit-profile-btn">
                      <Edit2 className="w-4 h-4 mr-2" />
                      {t('accountSettings.editProfile', 'Profili Düzenle')}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <Card>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {menuItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-secondary transition-colors"
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.count !== undefined && (
                          <Badge variant="secondary">{item.count}</Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                </nav>
              </CardContent>
            </Card>

            {/* Seller/Admin links */}
            {(user?.role === 'seller' || user?.role === 'admin') && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-base">{t('dashboard.quickAccess')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {user?.role === 'seller' && (
                    <Link to="/seller">
                      <Button variant="outline" className="w-full justify-start" data-testid="seller-dashboard-link">
                        {t('dashboard.sellerDashboard')}
                      </Button>
                    </Link>
                  )}
                  {user?.role === 'admin' && (
                    <Link to="/admin">
                      <Button variant="outline" className="w-full justify-start" data-testid="admin-dashboard-link">
                        {t('dashboard.adminPanel')}
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent orders */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('dashboard.recentOrders')}</CardTitle>
                <Link to="/orders">
                  <Button variant="ghost" size="sm">{t('common.viewAll')}</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-20" />
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">{t('dashboard.noOrdersYet')}</p>
                    <Link to="/products">
                      <Button variant="link" className="mt-2">Start Shopping</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <Link
                        key={order.id}
                        to={`/orders/${order.id}`}
                        className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                        data-testid={`recent-order-${order.id}`}
                      >
                        <div className="flex -space-x-2">
                          {order.items.slice(0, 2).map((item, i) => (
                            <div key={i} className="w-12 h-12 rounded-lg overflow-hidden border-2 border-white">
                              <ProductThumbnail
                                src={item.image}
                                alt={item.product_name ?? ''}
                                className="h-full w-full"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">Order #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatPrice(order.total)}</p>
                          <Badge variant="secondary" className="capitalize">{order.status}</Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">{orders.length}</p>
                    <p className="text-sm text-muted-foreground">{t('dashboard.totalOrders')}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">0</p>
                    <p className="text-sm text-muted-foreground">{t('dashboard.wishlistItems')}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary">0</p>
                    <p className="text-sm text-muted-foreground">{t('dashboard.reviews')}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
